/**
 * Picking up a new version without asking the person to load the page twice.
 *
 * The service worker precaches `index.html` and serves it for every navigation, so after a deploy
 * the browser keeps showing the old app until it is reloaded AFTER the new worker has taken
 * control. That is the two-loads tax we have been paying on every release — and the reason a screen
 * that is live can look like it "doesn't load".
 *
 * A reload is not free: the chat's conversation lives in memory and would be lost, so this never
 * reloads under someone's hands. It waits for a moment when nothing is lost — the page hidden, or
 * a navigation the person is making anyway — and takes it.
 */

export interface UpdateWatcher {
  /** True once a new worker has taken control and the page is still showing the old one. */
  pending: () => boolean;
  stop: () => void;
}

interface Deps {
  reload: () => void;
  isHidden: () => boolean;
}

/**
 * Whether to reload right now.
 *
 * `hadController` is the whole trick: the first visit of all fires `controllerchange` too, and
 * reloading then would bounce a page that is already the newest one.
 */
export function shouldReloadNow({
  hadController,
  hidden,
}: {
  hadController: boolean;
  hidden: boolean;
}): boolean {
  return hadController && hidden;
}

export function watchForServiceWorkerUpdate(
  { reload, isHidden }: Deps = { reload: () => window.location.reload(), isHidden: () => document.hidden },
): UpdateWatcher {
  const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  if (!sw) return { pending: () => false, stop: () => {} };

  // Taken before anything else: after the change, `controller` is the new worker either way.
  const hadController = Boolean(sw.controller);
  let pending = false;
  let reloaded = false;

  const reloadOnce = () => {
    if (reloaded) return;
    reloaded = true;
    reload();
  };

  const onControllerChange = () => {
    if (!hadController) return;
    pending = true;
    if (isHidden()) reloadOnce();
  };

  // The two moments where a reload costs nothing: the page is put away, or the person is leaving
  // this screen for another one anyway.
  const onVisibilityChange = () => {
    if (pending && isHidden()) reloadOnce();
  };
  const onHashChange = () => {
    if (pending) reloadOnce();
  };

  sw.addEventListener("controllerchange", onControllerChange);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("hashchange", onHashChange);

  return {
    pending: () => pending,
    stop: () => {
      sw.removeEventListener("controllerchange", onControllerChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("hashchange", onHashChange);
    },
  };
}

export default watchForServiceWorkerUpdate;
